import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import PipelinesAPI from '../../api/pipelines';

// Mutation-types locais (sem tocar no mutation-types.js central -> isolado do core).
const types = {
  SET_PIPELINE_UI_FLAG: 'SET_PIPELINE_UI_FLAG',
  SET_PIPELINES: 'SET_PIPELINES',
  ADD_PIPELINE: 'ADD_PIPELINE',
  EDIT_PIPELINE: 'EDIT_PIPELINE',
  DELETE_PIPELINE: 'DELETE_PIPELINE',
  SET_PIPELINE_BOARD: 'SET_PIPELINE_BOARD',
  SET_PIPELINE_STAGE_ORDER: 'SET_PIPELINE_STAGE_ORDER',
  SET_PIPELINE_CARD_VALUE: 'SET_PIPELINE_CARD_VALUE',
};

// Reordena uma lista de stages segundo `stageIds`. Stages ausentes da lista de
// ids são mantidos ao final, preservando a ordem relativa atual (defensivo).
const sortStagesByIds = (stages, stageIds) => {
  const indexById = new Map(stageIds.map((id, index) => [id, index]));
  const rank = stage =>
    indexById.has(stage.id) ? indexById.get(stage.id) : Number.MAX_SAFE_INTEGER;
  return [...stages].sort((a, b) => rank(a) - rank(b));
};

// Snapshot da ordem atual dos stages de um pipeline (para reverter no erro).
// Prefere o `board` quando ele aponta para o mesmo pipeline; senão usa o record.
const currentStageOrder = (_state, pipelineId) => {
  if (_state.board.id === pipelineId && _state.board.stages.length) {
    return _state.board.stages.map(stage => stage.id);
  }
  const record = _state.records.find(pipeline => pipeline.id === pipelineId);
  return record ? record.stages.map(stage => stage.id) : [];
};

export const state = {
  records: [],
  board: { id: null, name: '', stages: [] },
  uiFlags: { isFetching: false, isFetchingBoard: false },
};

export const getters = {
  getPipelines(_state) {
    return _state.records;
  },
  getBoard(_state) {
    return _state.board;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_PIPELINE_UI_FLAG, { isFetching: true });
    try {
      const response = await PipelinesAPI.get();
      commit(types.SET_PIPELINES, response.data);
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, pipelineObj) => {
    const response = await PipelinesAPI.create({ pipeline: pipelineObj });
    commit(types.ADD_PIPELINE, response.data);
    return response.data;
  },
  update: async ({ commit }, { id, ...pipelineObj }) => {
    const response = await PipelinesAPI.update(id, { pipeline: pipelineObj });
    commit(types.EDIT_PIPELINE, response.data);
  },
  delete: async ({ commit }, id) => {
    await PipelinesAPI.delete(id);
    commit(types.DELETE_PIPELINE, id);
  },
  // Aceita tanto `getBoard(pipelineId)` (uso legado) quanto
  // `getBoard({ pipelineId, inboxId, contactId })`. Os filtros vão como
  // query params para o contrato `GET .../board?inbox_id=&contact_id=`.
  getBoard: async ({ commit }, payload) => {
    const { pipelineId, inboxId, contactId } =
      typeof payload === 'object' && payload !== null
        ? payload
        : { pipelineId: payload };
    const params = {};
    if (inboxId) params.inbox_id = inboxId;
    if (contactId) params.contact_id = contactId;
    commit(types.SET_PIPELINE_UI_FLAG, { isFetchingBoard: true });
    try {
      const response = await PipelinesAPI.board(pipelineId, params);
      commit(types.SET_PIPELINE_BOARD, response.data);
      return response.data;
    } finally {
      commit(types.SET_PIPELINE_UI_FLAG, { isFetchingBoard: false });
    }
  },
  createStage: async ({ dispatch }, { pipelineId, ...stage }) => {
    await PipelinesAPI.createStage(pipelineId, { pipeline_stage: stage });
    await dispatch('get');
  },
  // Edita um estágio existente (nome, etiquetas, comportamento, WIP, rotting,
  // probabilidade). O backend (PipelineStagesController#update) é multi-tenant e
  // filtra label_ids por Current.account. Recarrega a lista para refletir a mudança.
  updateStage: async ({ dispatch }, { pipelineId, id, ...stage }) => {
    await PipelinesAPI.updateStage(pipelineId, id, { pipeline_stage: stage });
    await dispatch('get');
  },
  deleteStage: async ({ dispatch }, { pipelineId, id }) => {
    await PipelinesAPI.deleteStage(pipelineId, id);
    await dispatch('get');
  },
  moveCard: async (
    _store,
    { pipelineId, conversationId, fromStageId, toStageId }
  ) => {
    const response = await PipelinesAPI.moveCard(pipelineId, {
      conversation_id: conversationId,
      from_stage_id: fromStageId,
      to_stage_id: toStageId,
    });
    return response.data;
  },
  // Define o valor monetário do negócio do card (0/vazio remove). Atualiza o
  // board em memória com o valor retornado pelo backend para refletir na hora,
  // sem precisar recarregar tudo. Os totais por estágio/previsão são derivados
  // pelo backend, então quem precisa deles (cabeçalho/métricas) recarrega.
  updateCard: async ({ commit }, { pipelineId, conversationId, value }) => {
    const response = await PipelinesAPI.updateCard(pipelineId, {
      conversation_id: conversationId,
      value,
    });
    commit(types.SET_PIPELINE_CARD_VALUE, {
      conversationId,
      value: response.data.value,
    });
    return response.data;
  },
  // Métricas agregadas do pipeline (win_rate, previsão, valores em aberto,
  // por estágio). Não persiste no state — o painel consome o retorno direto.
  fetchMetrics: async (_store, { pipelineId, ...params }) => {
    const response = await PipelinesAPI.metrics(pipelineId, params);
    return response.data;
  },
  // Oportunidades do contato/conversa (todos os pipelines). Não persiste no
  // state — o widget do painel do contato consome a lista direto do retorno.
  // Aceita `{ contactId }` e/ou `{ conversationId }`; mapeia para os query
  // params do contrato (`contact_id` / `conversation_id`).
  fetchOpportunities: async (_store, { contactId, conversationId } = {}) => {
    const params = {};
    if (contactId) params.contact_id = contactId;
    if (conversationId) params.conversation_id = conversationId;
    const response = await PipelinesAPI.opportunities(params);
    return response.data.opportunities || [];
  },
  // Update OTIMISTA da ordem dos stages: aplica localmente, chama a API e, em
  // caso de erro, REVERTE para a ordem anterior antes de re-lançar.
  reorderStages: async (
    { commit, state: _state },
    { pipelineId, stageIds }
  ) => {
    const previousOrder = currentStageOrder(_state, pipelineId);
    commit(types.SET_PIPELINE_STAGE_ORDER, { pipelineId, stageIds });
    try {
      await PipelinesAPI.reorderStages(pipelineId, stageIds);
    } catch (error) {
      commit(types.SET_PIPELINE_STAGE_ORDER, {
        pipelineId,
        stageIds: previousOrder,
      });
      throw error;
    }
  },
};

export const mutations = {
  [types.SET_PIPELINE_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_PIPELINES]: MutationHelpers.set,
  [types.ADD_PIPELINE]: MutationHelpers.create,
  [types.EDIT_PIPELINE]: MutationHelpers.update,
  [types.DELETE_PIPELINE]: MutationHelpers.destroy,
  [types.SET_PIPELINE_BOARD](_state, board) {
    _state.board = board;
  },
  // Atualiza o `value` de uma conversa onde quer que ela esteja no board.
  [types.SET_PIPELINE_CARD_VALUE](_state, { conversationId, value }) {
    (_state.board.stages || []).forEach(stage => {
      const card = (stage.conversations || []).find(
        conversation => conversation.id === conversationId
      );
      if (card) card.value = value;
    });
  },
  // Reordena os stages do pipeline informado, tanto no board em foco quanto no
  // record correspondente, mantendo as duas telas (board e lista) sincronizadas.
  [types.SET_PIPELINE_STAGE_ORDER](_state, { pipelineId, stageIds }) {
    if (_state.board.id === pipelineId && _state.board.stages.length) {
      _state.board.stages = sortStagesByIds(_state.board.stages, stageIds);
    }
    const record = _state.records.find(pipeline => pipeline.id === pipelineId);
    if (record && record.stages) {
      record.stages = sortStagesByIds(record.stages, stageIds);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
